/**
 * Important: The order of the keys in these maps determine the order in which the results are displayed.
 *
 * Use the {@link sortResults}, {@link getAttributeForKey} and {@link getAttributesForKeys} functions to ensure the order of
 * the properties and results are consistent.
 *
 * @type {Object<string, Map<string, *>>}
 */
export const testDisplaySettings = {
    'example-test': new Map([
        ['yes', { label: 'Ja' }],
        ['no', { label: 'Nee' }],
    ]),
    'cl4l-life-skills-assessment-2024-08-27': new Map([
        ['problem-solving', { label: 'Probleemoplossend vermogen' }],
        ['critical-thinking', { label: 'Kritisch denkvermogen' }],
        ['effective-communication-skills', { label: 'Effectieve communicatievaardigheden' }],
        ['decision-making', { label: 'Besluitvorming' }],
        ['creative-thinking', { label: 'Creatief denkvermogen' }],
        ['interpersonal-relationship-skills', { label: 'Interpersoonlijke vaardigheden' }],
        ['self-awareness-building-skills', { label: 'Zelfbewustzijn' }],
        ['empathy', { label: 'Empathie' }],
        ['coping-with-stress', { label: 'Omgaan met stress' }],
        ['coping-with-emotions', { label: 'Omgaan met emoties' }],
    ]),
    'job-personality-study-choice-test-2024-05-16': new Map([
        ['social', { label: 'Sociaal' }],
        ['artistic', { label: 'Artistiek' }],
        ['realistic', { label: 'Realistisch' }],
        ['conventional', { label: 'Conventioneel' }],
        ['enterprising', { label: 'Ondernemend' }],
        ['intellectual', { label: 'Intellectueel' }],
    ]),
    'job-personality-personality-test-2024-05-16': new Map([
        [
            'emotional_stability',
            {
                label: 'Emotionele stabiliteit',
                color: '#3EB3A0',
            },
        ],
        [
            'extroversion',
            {
                label: 'Extraversie',
                color: '#3498DC',
            },
        ],
        [
            'orderliness',
            {
                label: 'Ordelijkheid',
                color: '#9B58B5',
            },
        ],
        [
            'friendliness',
            {
                label: 'Vriendelijkheid',
                color: '#E67F23',
            },
        ],
        [
            'openness',
            {
                label: 'Openheid',
                color: '#E74D3D',
            },
        ],
    ]),
    'job-personality-mbo-occupation-test-2024-05-16': new Map([
        ['social', { label: 'Sociaal' }],
        ['artistic', { label: 'Artistiek' }],
        ['realistic', { label: 'Realistisch' }],
        ['conventional', { label: 'Conventioneel' }],
        ['enterprising', { label: 'Ondernemend' }],
        ['intellectual', { label: 'Intellectueel' }],
    ]),
    'job-personality-profile-choice-test-2024-06-12': new Map([
        ['culture-and-society', { label: 'Cultuur en Maatschappij', abbreviation: 'C&M', order: 3 }],
        ['economy-and-society', { label: 'Economie en Maatschappij', abbreviation: 'E&M', order: 2 }],
        ['nature-and-health', { label: 'Natuur en Gezondheid', abbreviation: 'N&G', order: 1 }],
        ['nature-and-technology', { label: 'Natuur en Techniek', abbreviation: 'N&T', order: 0 }],
    ]),
    'lobx-job-event-test-2024-12-19': new Map([
        ['realistic', { label: 'Het Realistische type; “de doener”' }],
        ['investigative', { label: 'Het explorerende type; “de onderzoeker”' }],
        ['artistic', { label: 'Het artistieke type; “de creatieveling”' }],
        ['social', { label: 'Het sociale type; “de helper”' }],
        ['conventional', { label: 'Het conventionele type; “de organisator”' }],
        ['enterprising', { label: 'Het ondernemende type; “de overtuiger”' }],
    ]),
    'job-personality-cultural-type-test-2024-12-16': new Map([
        ['cultural_interest', { label: 'Culturele interesse' }],
        ['cultural_activity', { label: 'Culturele activiteit' }],
    ]),
    'job-personality-cultural-interest-test-2024-12-17': new Map([
        ['literature', { label: 'Literatuur', order: 1 }],
        ['visual_arts', { label: 'Beeldende kunst', order: 2 }],
        ['film_and_media', { label: 'Film en media', order: 3 }],
        ['music_and_performance', { label: 'Muziek en performance', order: 4 }],
        ['design_and_architecture', { label: 'Design en architectuur', order: 5 }],
        ['cultural_traditions', { label: 'Culturele tradities', order: 6 }],
        ['adventure_and_world_cultures', { label: 'Avontuur en wereldculturen', order: 7 }],
        ['social_media', { label: 'Sociale media', order: 8 }],
    ]),
};

/**
 * Get the value of an attribute for a specific key of a test
 *
 * @param {string} testKey
 * @param {string} key
 * @param {string} attribute
 * @return {*}
 */
export function getAttributeForKey(testKey, key, attribute) {
    if (!testDisplaySettings[testKey]) throw new Error(`No test found with key ${testKey}`);

    return testDisplaySettings[testKey].get(key)[attribute];
}

/**
 * Get the values of an attribute for all keys of a test
 *
 * @param {string} testKey
 * @param {string} attributeKey
 * @return {Array<*>}
 */
export function getAttributesForKeys(testKey, attributeKey) {
    if (!testDisplaySettings[testKey]) throw new Error(`No test found with key ${testKey}`);

    return Array.from(testDisplaySettings[testKey], ([key, value]) => value[attributeKey]);
}

/**
 * Get the keys of a test with the chosen attribute values
 *
 * @param {string} testKey
 * @param {string} attributeKey
 * @return {Object<string, *>}
 */
export function getKeysWithAttribute(testKey, attributeKey) {
    if (!testDisplaySettings[testKey]) throw new Error(`No test found with key ${testKey}`);

    return Array.from(testDisplaySettings[testKey]).reduce((acc, [key, value]) => {
        acc[key] = value[attributeKey];
        return acc;
    }, {});
}

/**
 * Sort the results of a test based on the order of the keys in the testDisplaySettings
 *
 * @param {string} testKey
 * @param {Object} resultData
 * @return {array<*>} The values of the resultData sorted by the order of the keys in the testDisplaySettings
 */
export function sortResults(testKey, resultData) {
    if (!testDisplaySettings[testKey]) throw new Error(`No test found with key ${testKey}`);

    return Array.from(testDisplaySettings[testKey], ([key, _]) => key).map((key) => resultData[key]);
}
